// src/vanilla/ref.ts
var internal_refSet = /* @__PURE__ */ new WeakSet();
function ref(o) {
  internal_refSet.add(o);
  return o;
}
function isRef(k) {
  return internal_refSet.has(k);
}
function deepCloneWithRef(initialState) {
  const cloned = createObjectFromPrototype(initialState);
  for (const key in initialState) {
    const value = initialState[key];
    if (isObject(value)) {
      if (isRef(value)) {
        cloned[key] = value;
      } else {
        cloned[key] = deepCloneWithRef(value);
      }
    } else {
      cloned[key] = value;
    }
  }
  return cloned;
}

// src/utils/react-fast-compare.ts
var hasMap = typeof Map === "function";
var hasSet = typeof Set === "function";
var hasHTMLElementType = typeof HTMLElement !== "undefined";
var hasArrayBuffer = typeof ArrayBuffer === "function" && !!ArrayBuffer.isView;
function reactFastCompare(objA, objB) {
  try {
    return equal(objA, objB);
  } catch (error) {
    if ((error.message || "").match(/stack|recursion/i)) {
      console.warn("react-fast-compare cannot handle circular refs");
      return false;
    }
    throw error;
  }
}
function equal(objA, objB) {
  if (objA === objB) return true;
  if (objA && objB && typeof objA === "object" && typeof objB === "object") {
    if (objA.constructor !== objB.constructor) return false;
    let length;
    let i;
    let keys;
    if (Array.isArray(objA)) {
      length = objA.length;
      if (length !== objB.length) return false;
      for (i = length; i-- !== 0; ) if (!equal(objA[i], objB[i])) return false;
      return true;
    }
    let it;
    if (hasMap && objA instanceof Map && objB instanceof Map) {
      if (objA.size !== objB.size) return false;
      it = objA.entries();
      while (!(i = it.next()).done) if (!objB.has(i.value[0])) return false;
      it = objA.entries();
      while (!(i = it.next()).done) if (!equal(i.value[1], objB.get(i.value[0]))) return false;
      return true;
    }
    if (hasSet && objA instanceof Set && objB instanceof Set) {
      if (objA.size !== objB.size) return false;
      it = objA.entries();
      while (!(i = it.next()).done) if (!objB.has(i.value[0])) return false;
      return true;
    }
    if (hasArrayBuffer && ArrayBuffer.isView(objA) && ArrayBuffer.isView(objB)) {
      length = objA?.length;
      if (length !== objB?.length) return false;
      for (i = length; i-- !== 0; ) if (objA[i] !== objB[i]) return false;
      return true;
    }
    if (objA.constructor === RegExp) return objA.source === objB.source && objA.flags === objB.flags;
    if (objA.valueOf !== Object.prototype.valueOf && typeof objA.valueOf === "function" && typeof objB.valueOf === "function")
      return objA.valueOf() === objB.valueOf();
    if (objA.toString !== Object.prototype.toString && typeof objA.toString === "function" && typeof objB.toString === "function")
      return objA.toString() === objB.toString();
    keys = Object.keys(objA);
    length = keys.length;
    if (length !== Object.keys(objB).length) return false;
    for (i = length; i-- !== 0; ) if (!Object.prototype.hasOwnProperty.call(objB, keys[i])) return false;
    if (hasHTMLElementType && objA instanceof HTMLElement) return false;
    for (i = length; i-- !== 0; ) {
      if ((keys[i] === "_owner" || keys[i] === "__v" || keys[i] === "__o") && objA.$$typeof) {
        continue;
      }
      if (!equal(objA[keys[i]], objB[keys[i]])) return false;
    }
    return true;
  }
  return objA !== objA && objB !== objB;
}

// src/utils/index.ts
var SNAPSHOT = Symbol.for("SNAPSHOT");
var LISTENERS = Symbol.for("LISTENERS");
var REACTIVE = Symbol.for("REACTIVE");
var PROXY_COUNT = Symbol.for("PROXY_COUNT");
var isProduction = process.env.NODE_ENV === "production";
var noop = () => {
};
var hasOwn = Object.prototype.hasOwnProperty;
var isObject = (x) => typeof x === "object" && x !== null;
var isBoolean = (x) => typeof x === "boolean";
var isFunction = (x) => typeof x === "function";
function createObjectFromPrototype(target) {
  return Array.isArray(target) ? [] : Object.create(Object.getPrototypeOf(target));
}
function canProxy(x) {
  return isObject(x) && !isRef(x) && (Array.isArray(x) || !(Symbol.iterator in x)) && !(x instanceof WeakMap) && !(x instanceof WeakSet) && !(x instanceof Error) && !(x instanceof Number) && !(x instanceof Date) && !(x instanceof String) && !(x instanceof RegExp) && !(x instanceof ArrayBuffer) && !(x instanceof Promise) && // from node 18 & 20
  (typeof File === "undefined" || !(x instanceof File)) && // from node 18
  (typeof Blob === "undefined" || !(x instanceof Blob)) && // from node 18
  (typeof FormData === "undefined" || !(x instanceof FormData));
}
var numberReg = /^\d+$/;
function propertyKeysToPath(keys) {
  let path = "";
  const { length } = keys;
  for (let i = 0; i < length; i++) {
    const key = keys[i];
    if (typeof key === "number" || typeof key === "string" && numberReg.test(key)) {
      path += `[${key}]`;
    } else {
      path += (i === 0 ? "" : ".") + String(key);
    }
  }
  return path;
}
function get(object, path, defaultValue = void 0) {
  const keys = Array.isArray(path) ? path : [path];
  for (const key of keys) {
    if (!(key in object)) return defaultValue;
    object = object[key];
  }
  return object;
}
function shallowEqual(objA, objB) {
  if (Object.is(objA, objB)) {
    return true;
  }
  if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
    return false;
  }
  if (objA instanceof Map && objB instanceof Map) {
    if (objA.size !== objB.size) return false;
    for (const [key, value] of objA) {
      if (!Object.is(value, objB.get(key))) {
        return false;
      }
    }
    return true;
  }
  if (objA instanceof Set && objB instanceof Set) {
    if (objA.size !== objB.size) return false;
    for (const value of objA) {
      if (!objB.has(value)) {
        return false;
      }
    }
    return true;
  }
  const keysA = Object.keys(objA);
  if (keysA.length !== Object.keys(objB).length) {
    return false;
  }
  const keysLength = keysA.length;
  for (let i = 0; i < keysLength; i++) {
    if (!hasOwn.call(objB, keysA[i]) || !Object.is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}
function deepEqual(objA, objB) {
  return reactFastCompare(objA, objB);
}

// src/vanilla/snapshot.ts
function snapshot(proxyState, selector = (s) => s) {
  return selector(proxyState[SNAPSHOT]);
}
var getSnapshot = snapshot;

// src/enhancers/vanilla/with-snapshot.ts
function withSnapshot(store) {
  const _snapshot = (selector = (s) => s) => {
    return snapshot(store.mutate, selector);
  };
  return {
    ...store,
    snapshot: _snapshot
  };
}

// src/vanilla/subscribe.ts
var globalNotifyInSync = false;
function setGlobalNotifyInSync(value) {
  globalNotifyInSync = value;
}
function subscribe(proxyState, callback, notifyInSync) {
  let promise;
  let previousState = snapshot(proxyState);
  const callbacks = /* @__PURE__ */ new Set();
  const runCallbacks = () => {
    for (const callback2 of callbacks) {
      callback2();
    }
    callbacks.clear();
    previousState = snapshot(proxyState);
  };
  function listener(props, version) {
    const currentState = snapshot(proxyState);
    const changes = {
      props,
      propsPath: propertyKeysToPath(props),
      previous: get(previousState, props),
      current: get(currentState, props),
      snapshot: currentState
    };
    callbacks.add(() => void callback(changes, version));
    const finalNotifyInSync = isBoolean(notifyInSync) ? notifyInSync : globalNotifyInSync;
    if (finalNotifyInSync) {
      return void runCallbacks();
    }
    if (!promise) {
      promise = Promise.resolve().then(() => {
        promise = void 0;
        runCallbacks();
      });
    }
  }
  ;
  proxyState[LISTENERS].add(listener);
  return () => {
    ;
    proxyState[LISTENERS].delete(listener);
  };
}

// src/enhancers/vanilla/with-subscribe.ts
function withSubscribe(store) {
  function boundSubscribe(listener, sync, selector = (s) => s) {
    return subscribe(selector(store.mutate), listener, sync);
  }
  return {
    ...store,
    subscribe: boundSubscribe
  };
}

// src/vanilla/proxy.ts
var globalVersion = 1;
var snapshotCache = /* @__PURE__ */ new WeakMap();
function proxy(initState, parentProps = [], options) {
  const { addParentProxyCount = () => {
  } } = options || {};
  let version = globalVersion;
  const listeners = /* @__PURE__ */ new Set();
  const propListenerMap = /* @__PURE__ */ new Map();
  const notifyUpdate = (props, nextVersion = ++globalVersion) => {
    if (version !== nextVersion) {
      version = nextVersion;
      for (const callback of listeners) {
        callback(props, version);
      }
    }
  };
  const getPropListener = (prop) => {
    let listener = propListenerMap.get(prop);
    if (!listener) {
      listener = (props) => void notifyUpdate(props);
      propListenerMap.set(prop, listener);
    }
    return listener;
  };
  const popPropListener = (prop) => {
    const listener = propListenerMap.get(prop);
    propListenerMap.delete(prop);
    return listener;
  };
  const createSnapshot = (target, receiver) => {
    const cache = snapshotCache.get(receiver);
    if (cache?.[0] === version) return cache[1];
    const nextSnapshot = createObjectFromPrototype(target);
    snapshotCache.set(receiver, [version, nextSnapshot]);
    for (const key of Reflect.ownKeys(target)) {
      if (key === REACTIVE || key === PROXY_COUNT) continue;
      const value = Reflect.get(target, key, receiver);
      if (value?.[REACTIVE]) {
        nextSnapshot[key] = snapshot(value);
      } else {
        nextSnapshot[key] = value;
      }
    }
    Object.preventExtensions(nextSnapshot);
    return nextSnapshot;
  };
  const baseObject = createObjectFromPrototype(initState);
  const addCurrentProxyCount = (value = 1) => {
    addParentProxyCount(value);
    const count = Reflect.get(proxyState, PROXY_COUNT) || 1;
    Reflect.defineProperty(proxyState, PROXY_COUNT, { value: count + value, writable: true });
  };
  const proxyState = new Proxy(baseObject, {
    get(target, prop, receiver) {
      if (prop === LISTENERS) {
        return listeners;
      }
      if (prop === SNAPSHOT) {
        return createSnapshot(target, receiver);
      }
      return Reflect.get(target, prop, receiver);
    },
    set(target, prop, value, receiver) {
      const props = [...parentProps, prop];
      const preValue = Reflect.get(target, prop, receiver);
      const childListeners = preValue?.[LISTENERS];
      if (childListeners) {
        childListeners.delete(popPropListener(prop));
      }
      if (!isObject(value) && Object.is(preValue, value)) {
        return true;
      }
      let nextValue = value;
      if (nextValue?.[LISTENERS]) {
        nextValue[LISTENERS].add(getPropListener(prop));
      } else if (canProxy(value)) {
        if (preValue?.[REACTIVE]) {
          const childrenCount = preValue?.[PROXY_COUNT] || 1;
          addCurrentProxyCount(-childrenCount);
        }
        addCurrentProxyCount(1);
        nextValue = proxy(value, props, {
          addParentProxyCount: addCurrentProxyCount
        });
        nextValue[LISTENERS].add(getPropListener(prop));
      }
      const success = Reflect.set(target, prop, nextValue, receiver);
      success && notifyUpdate(props);
      return success;
    },
    deleteProperty(target, prop) {
      const props = [...parentProps, prop];
      const preValue = Reflect.get(target, prop);
      const childListeners = preValue?.[LISTENERS];
      if (childListeners) {
        childListeners.delete(popPropListener(prop));
      }
      if (preValue?.[REACTIVE]) {
        const childrenCount = preValue?.[PROXY_COUNT] || 1;
        addCurrentProxyCount(-childrenCount);
      }
      const success = Reflect.deleteProperty(target, prop);
      success && notifyUpdate(props);
      return success;
    }
  });
  Reflect.defineProperty(proxyState, REACTIVE, { value: true });
  for (const key of Reflect.ownKeys(initState)) {
    proxyState[key] = initState[key];
  }
  if (!Reflect.has(proxyState, PROXY_COUNT)) {
    Reflect.defineProperty(proxyState, PROXY_COUNT, { value: 1, writable: true });
  }
  return proxyState;
}

// src/vanilla/create.ts
function createVanilla(initState, _options = {}) {
  const proxyState = proxy(initState);
  function restore(options = {}) {
    const { exclude = [] } = options;
    const clonedState = deepCloneWithRef(initState);
    const initialKeys = Object.keys(initState);
    for (const key of Object.keys(clonedState)) {
      if (exclude.includes(key)) continue;
      proxyState[key] = clonedState[key];
    }
    const addedKeys = Object.keys(proxyState).filter((key) => !initialKeys.includes(key));
    for (const key of addedKeys) {
      if (exclude.includes(key)) continue;
      delete proxyState[key];
    }
  }
  const store = { mutate: proxyState, restore };
  return withSubscribe(withSnapshot(store));
}
var create = createVanilla;

// src/vanilla/devtools.ts
function devtools(store, options) {
  if (isProduction) return noop;
  const ext = window.__REDUX_DEVTOOLS_EXTENSION__;
  const enable = options?.enable ?? true;
  let isMutatingStoreByDevtools = false;
  if (!enable) return noop;
  if (!ext) {
    const infos = [
      "[Warning] to enable devtools, make sure you've installed Redux devtools extension \u{1F449}",
      "https://github.com/reduxjs/redux-devtools#redux-devtools"
    ];
    console.warn(infos.join(" "));
    return noop;
  }
  const name = options?.name ?? "untitled";
  const devtools2 = ext.connect(options);
  const initialState = snapshot(store.mutate);
  devtools2.init(initialState);
  devtools2.subscribe((message) => {
    console.debug("[reactive] message from devtools: ", message);
    if (message.type !== "DISPATCH") return;
    if (!message.payload) return;
    if (message.payload.type === "RESET") devtools2.init(initialState);
    if (message.payload.type === "COMMIT") devtools2.init(snapshot(store.mutate));
    const actions = ["ROLLBACK", "JUMP_TO_ACTION"];
    const isAction = actions.includes(message.payload.type);
    const hasState = message.state && message.state !== "{}";
    if (isAction && hasState) {
      try {
        isMutatingStoreByDevtools = true;
        const newState = JSON.parse(message.state || "{}");
        Object.assign(store.mutate, newState);
        console.debug("[reactive] mutate state by devtools: ", newState);
      } catch (e) {
        devtools2.error(e?.message || e?.toString() || JSON.stringify(e || ""));
      } finally {
        isMutatingStoreByDevtools = false;
      }
    }
  });
  const unsubscribe = subscribe(
    store.mutate,
    (changes, version) => {
      const { propsPath, previous, current, snapshot: snapshot2 } = changes;
      if (isMutatingStoreByDevtools) return;
      const payload = {
        type: `[${canProxy(current) ? "replace" : "set"}] ${propsPath}`,
        payload: current,
        _previousValue: previous,
        _updatedAt: (/* @__PURE__ */ new Date()).toLocaleString(),
        _innerVersion: version
      };
      devtools2.send(payload, snapshot2);
      console.debug(`[reactive] [${name}] [${getActionType(current)}] ${propsPath}`, current);
    },
    true
  );
  console.debug(`[reactive] [${name}] devtools is enabled`);
  return () => {
    devtools2.unsubscribe();
    unsubscribe();
    console.debug(`[reactive] [${name}] devtools is disabled`);
  };
}
function getActionType(state) {
  return canProxy(state) ? "replace" : "set";
}

// src/vanilla/produce.ts
function produce(obj, draftHandler) {
  let state = proxy(obj);
  draftHandler(state);
  const nextSnapshot = snapshot(state);
  state = null;
  return nextSnapshot;
}

// src/vanilla/proxy-count.ts
function proxyCount(proxyState) {
  return proxyState?.[PROXY_COUNT] ?? 0;
}

// src/enhancers/vanilla/with-derived.ts
function withDerived(store, mapFn = (s) => s) {
  return {
    ...store,
    derived: () => mapFn(store.mutate)
  };
}

export {
  ref,
  isRef,
  deepCloneWithRef,
  isFunction,
  shallowEqual,
  deepEqual,
  snapshot,
  getSnapshot,
  withSnapshot,
  setGlobalNotifyInSync,
  subscribe,
  withSubscribe,
  proxy,
  createVanilla,
  create,
  devtools,
  produce,
  proxyCount,
  withDerived
};
